/**
 * @license
 * Copyright (c) 2021-present, FriendliAI Inc. All rights reserved.
 */

import { useMantineTheme } from '@mantine/core';
import { RiAlertFill, RiLoader4Line } from '@remixicon/react';
import { useTranslation } from 'react-i18next';

import { DedicatedDatasetStatus } from '@fai/friendli-suite/generated/client/graphql';
import { rotateIcon } from '@fai/ui/f.css';

import type { DatasetStatusVariants } from './model';

export const useDatasetStatusVariants = (): DatasetStatusVariants => {
  const { t } = useTranslation();
  const theme = useMantineTheme();

  return {
    [DedicatedDatasetStatus.Uploading]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.dataset_status_uploading'),
      color: theme.pf.color.fg.warning.default.press,
      backgroundColor: theme.pf.color.bg.warning.default.base,
      borderColor: theme.colors.yellow[4],
    },
    [DedicatedDatasetStatus.Validating]: {
      icon: <RiLoader4Line className={rotateIcon} size={16} />,
      content: t('dedicated_endpoints.project.dataset_status_validating'),
      color: theme.pf.color.fg.warning.default.press,
      backgroundColor: theme.pf.color.bg.warning.default.base,
      borderColor: theme.colors.yellow[4],
    },
    [DedicatedDatasetStatus.Created]: {
      icon: null,
      content: t('dedicated_endpoints.project.dataset_status_ready'),
      color: theme.pf.color.fg.apigreen.base,
      backgroundColor: theme.pf.color.bg.success.default.base,
      borderColor: theme.colors.lime[4],
    },
    [DedicatedDatasetStatus.Failed]: {
      icon: <RiAlertFill size={16} />,
      content: t('dedicated_endpoints.project.dataset_status_failed'),
      color: theme.pf.color.fg.danger.default.hover,
      backgroundColor: theme.pf.color.bg.danger.default.base,
      borderColor: theme.colors.red[3],
    },
    [DedicatedDatasetStatus.Deleted]: {
      icon: null,
      content: t('dedicated_endpoints.project.dataset_status_deleted'),
      color: theme.pf.color.fg.neutral.default,
      backgroundColor: theme.pf.color.bg.neutral.subtle.hover,
      borderColor: theme.colors.gray[4],
    },
  };
};
